import { StableKey } from 'app/models/common-types'
import { EntityParams } from 'app/models/entity-params'
import {
  AllocationAmountUpdate,
  ChangeSubTradeData,
  columnDefSubTradeAllocation,
  FacilityAllocationAction,
  FacilityAllocationSummary,
  SaveAllocationRow,
  SubTradeAllocation,
  SubTradeParams,
} from 'app/models/sub-trade-params'
import { TradedFacilityParams } from 'app/models/traded-facilities-params'

export const sortBySystemId = (
  a: EntityParams,
  b: EntityParams,
  refinedSubTrades: SubTradeParams[],
) => {
  const systemIdA =
    refinedSubTrades
      .find(st => st.buyerId?.id === a.id || st.sellerId?.id === a.id)
      ?.systemId?.split('-')
      .pop() || ''
  const systemIdB =
    refinedSubTrades
      .find(st => st.buyerId?.id === b.id || st.sellerId?.id === b.id)
      ?.systemId?.split('-')
      .pop() || ''
  if (!systemIdA && systemIdB) return 1
  if (systemIdA && !systemIdB) return -1
  return systemIdA.localeCompare(systemIdB)
}

export const generateUpdateAllocationsRowsForEntity = (
  entity: EntityParams,
  refinedSubTrades: SubTradeParams[],
  refinedFacilities: TradedFacilityParams[],
  masterTradeId: StableKey,
) => {
  const tradeForEntity = refinedSubTrades.find(
    st => st.buyerId?.id === entity.id || st.sellerId?.id === entity.id,
  )
  const systemId = tradeForEntity?.systemId?.split('-').pop() || ''
  // Otherwise, generate rows based on the trade data.
  return refinedFacilities.map((facility, index) => ({
    status: tradeForEntity?.status ?? 'Unallocated',
    id: '',
    subTradeId: index === 0 ? systemId : '',
    entityName: index === 0 ? entity.entityName || '' : '',
    clientId: index === 0 ? tradeForEntity?.clientId || '' : '',
    mei: index === 0 ? entity.mei || '' : '',
    facilityName: `${facility.facilityDetails?.name}(${facility.facilityDetails?.cusip || ''})`,
    amount:
      tradeForEntity?.tradedFacilities?.find(
        st => st.facilityId.id === facility.facilityId.id,
      )?.tradeAmount || 0,
    currency: facility.facilityDetails?.baseCurrency || 'USD',
    masterTradeId: masterTradeId,
    entityId: { id: entity.id || '', admin: entity.maker || '' },
    contractId: tradeForEntity?.tradedFacilities?.find(
      st => st.facilityId.id === facility.facilityId.id,
    )?.contractId ?? (tradeForEntity ? { id: tradeForEntity.id ?? '', admin: masterTradeId.admin } : undefined),
    facilityWithAmount: {
      amount: '0',
      total: facility.tradeAmount,
      facilityId: facility.facilityId,
    },
  }))
}

const roundToCents = (value: number) => Math.round(value * 100) / 100

const rowAmount = (row: columnDefSubTradeAllocation) => Number(row.amount) || 0

const facilityRowIndexes = (
  allocationData: columnDefSubTradeAllocation[],
  facilityId: string,
) =>
  allocationData.reduce<number[]>((indexes, row, index) => {
    if (row.facilityWithAmount?.facilityId?.id === facilityId) {
      indexes.push(index)
    }
    return indexes
  }, [])

export const getFacilityAllocationAction = (
  tradedAmount: number,
  totalAllocated: number,
  hasEditableRows: boolean,
): FacilityAllocationAction => {
  if (!hasEditableRows) return null
  const remaining = roundToCents(tradedAmount - totalAllocated)
  if (remaining > 0) return 'prorate'
  if (remaining === 0 && totalAllocated > 0) return 'unallocate'
  return null
}

/**
 * Split a facility's remaining balance evenly across the entities with no
 * amount allocated yet on that facility; when every entity already has an
 * amount, split across all of them (added on top) so the remainder still
 * clears. The split is done in whole cents with the residual cents spread
 * one-per-target from the first target, so the amounts always sum exactly
 * to the remaining balance.
 */
export const computeProratedAmounts = (
  allocationData: columnDefSubTradeAllocation[],
  facilityId: string,
): AllocationAmountUpdate[] => {
  const indexes = facilityRowIndexes(allocationData, facilityId)
  if (indexes.length === 0) return []

  const tradedAmount = roundToCents(
    Number(allocationData[indexes[0]].facilityWithAmount?.total) || 0,
  )
  const totalAllocated = indexes.reduce(
    (sum, index) => sum + rowAmount(allocationData[index]),
    0,
  )
  const remainingCents = Math.round((tradedAmount - totalAllocated) * 100)
  if (remainingCents <= 0) return []

  const editable = indexes.filter(
    index => allocationData[index].status !== 'Settled',
  )
  if (editable.length === 0) return []

  const zeroTargets = editable.filter(
    index => rowAmount(allocationData[index]) === 0,
  )
  const targets = zeroTargets.length > 0 ? zeroTargets : editable

  const baseCents = Math.floor(remainingCents / targets.length)
  const extraCents = remainingCents - baseCents * targets.length

  return targets.map((rowIndex, position) => {
    const shareCents = baseCents + (position < extraCents ? 1 : 0)
    return {
      rowIndex,
      amount: roundToCents(rowAmount(allocationData[rowIndex]) + shareCents / 100),
    }
  })
}

/** Reset every editable entity amount on the facility to 0. */
export const computeUnallocatedAmounts = (
  allocationData: columnDefSubTradeAllocation[],
  facilityId: string,
): AllocationAmountUpdate[] =>
  facilityRowIndexes(allocationData, facilityId)
    .filter(
      index =>
        allocationData[index].status !== 'Settled' &&
        rowAmount(allocationData[index]) !== 0,
    )
    .map(rowIndex => ({ rowIndex, amount: 0 }))

/**
 * Clamp an amount so the facility's total never exceeds its traded amount:
 * the ceiling is the traded amount minus what the other rows on the same
 * facility already hold (never below 0).
 */
export const clampAmountToFacilityRemaining = (
  allocationData: columnDefSubTradeAllocation[],
  rowIndex: number,
  proposedAmount: number,
): { amount: number; clamped: boolean } => {
  const row = allocationData[rowIndex]
  const facilityId = row?.facilityWithAmount?.facilityId?.id
  if (!facilityId || proposedAmount <= 0) {
    return { amount: proposedAmount, clamped: false }
  }

  const tradedAmount = roundToCents(Number(row.facilityWithAmount?.total) || 0)
  const othersTotal = facilityRowIndexes(allocationData, facilityId)
    .filter(index => index !== rowIndex)
    .reduce((sum, index) => sum + rowAmount(allocationData[index]), 0)
  const maxAllowed = Math.max(0, roundToCents(tradedAmount - othersTotal))

  if (roundToCents(proposedAmount) > maxAllowed) {
    return { amount: maxAllowed, clamped: true }
  }
  return { amount: proposedAmount, clamped: false }
}

export const buildFacilityAllocationSummaries = (
  allocationData: columnDefSubTradeAllocation[],
  refinedFacilities: TradedFacilityParams[],
): FacilityAllocationSummary[] =>
  refinedFacilities.map(facility => {
    const facilityId = facility.facilityId.id
    const indexes = facilityRowIndexes(allocationData, facilityId)
    const tradedAmount = roundToCents(Number(facility.tradeAmount) || 0)
    const totalAllocated = roundToCents(
      indexes.reduce((sum, index) => sum + rowAmount(allocationData[index]), 0),
    )
    const hasEditableRows = indexes.some(
      index => allocationData[index].status !== 'Settled',
    )
    return {
      facilityId,
      facilityName: `${facility.facilityDetails?.name}(${facility.facilityDetails?.cusip || ''})`,
      currency: facility.facilityDetails?.baseCurrency || 'USD',
      tradedAmount,
      totalAllocated,
      remaining: roundToCents(tradedAmount - totalAllocated),
      action: getFacilityAllocationAction(
        tradedAmount,
        totalAllocated,
        hasEditableRows,
      ),
    }
  })

export function transformDataAllocation(
  data: SaveAllocationRow[],
  masterTradeId: StableKey,
): SubTradeAllocation {
  // Group rows by a unique key derived from entityId
  const grouped: { [key: string]: ChangeSubTradeData } = {}
  data
    .filter(item => item.status !== 'Settled')
    .forEach((item: SaveAllocationRow) => {
      const key = `${item.entityId.id}|${item.entityId.admin}`
      if (!grouped[key]) {
        grouped[key] = {
          clientId: item.clientId,
          entityId: item.entityId,
          facilityWithAmount: [],
        }
      }
      // Only add facility data if the amount is non-zero.
      if (item.amount !== 0) {
        grouped[key].facilityWithAmount.push({
          facilityId: item.facilityWithAmount.facilityId,
          // Convert the numeric amount to a string as defined in facilityWithAmount type.
          amount: item.amount.toString(),
        })
      }
    })

  // Get all grouped ChangeSubTradeData objects that have at least one facility
  const changeSubTradeData: ChangeSubTradeData[] = Object.values(
    grouped,
  ).filter(group => group.facilityWithAmount.length > 0)

  return {
    changeSubTradeData,
    masterTradeId,
  }
}
